import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Modal,
  Form,
  Input,
  Switch,
  Radio,
  Button,
  Spin,
  message,
  Tooltip,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { logger } from '@src/utils/misc/logger';
import { useGenerateAnalysisTemplate } from './hooks/useGenerateAnalysisTemplate';
import { useSaveAnalysisTemplate } from './hooks/useSaveAnalysisTemplate';
import useCanManageSystemPrompt from '../SystemPrompt/hooks/useCanManageSysPrompt';
import TagInput from '../ManageAnalyses/TagInput';
import { canSeeDeepModeFeatures } from '../utils/deepModeAccess';

const { TextArea } = Input;
const { Text } = Typography;

interface SaveAnalysisDrawerProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  onSaveAndSchedule?: (item: { id: number; query: string }) => void;
}

export const SaveAnalysisDrawer: React.FC<SaveAnalysisDrawerProps> = ({
  open,
  onClose,
  sessionId,
  onSaveAndSchedule,
}) => {
  const [form] = Form.useForm();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState<{
    query: string;
    extra_instructions: string;
  } | null>(null);
  const [tags, setTags] = useState<string[]>([]);

  const generateMutation = useGenerateAnalysisTemplate();
  const saveMutation = useSaveAnalysisTemplate();
  const canManageSysPrompt = useCanManageSystemPrompt();
  const canSeeDeepMode = canSeeDeepModeFeatures();

  // Start generation when drawer opens
  useEffect(() => {
    if (open && !generatedData && !isGenerating) {
      setIsGenerating(true);
      generateMutation.mutate(
        { session_id: sessionId },
        {
          onSuccess: (data) => {
            setIsGenerating(false);
            if (data.status === 'success' && data.result) {
              setGeneratedData(data.result);
              // Pre-fill form with generated data
              form.setFieldsValue({
                query: data.result.query,
                extra_instructions: data.result.extra_instructions,
                deep_mode: false,
                visibility: 'private',
              });
            } else {
              message.error(data.error || 'Failed to generate template');
              onClose();
            }
          },
          onError: () => {
            setIsGenerating(false);
            onClose();
          },
        },
      );
    }
  }, [open, sessionId, generatedData, isGenerating]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await saveMutation.mutateAsync({
        ...values,
        deep_mode: canSeeDeepMode ? values.deep_mode : false,
        tags,
      });
      handleClose();
    } catch (error) {
      // Validation or save error
      logger.error('Save analysis template error:', error);
    }
  };

  const handleSaveAndSchedule = async () => {
    try {
      const values = await form.validateFields();
      const savedAnalysis = await saveMutation.mutateAsync({
        ...values,
        deep_mode: canSeeDeepMode ? values.deep_mode : false,
        tags,
      });
      handleClose();
      onSaveAndSchedule?.({ id: savedAnalysis.id, query: savedAnalysis.query });
    } catch (error) {
      logger.error('Save & Schedule error:', error);
    }
  };

  const handleClose = () => {
    setIsGenerating(false);
    setGeneratedData(null);
    setTags([]);
    form.resetFields();
    onClose();
  };

  const handleRetry = () => {
    setIsGenerating(false);
    setGeneratedData(null);
    // Re-trigger generation
    setIsGenerating(true);
    generateMutation.mutate(
      { session_id: sessionId },
      {
        onSuccess: (data) => {
          setIsGenerating(false);
          if (data.status === 'success' && data.result) {
            setGeneratedData(data.result);
            form.setFieldsValue({
              query: data.result.query,
              extra_instructions: data.result.extra_instructions,
              deep_mode: false,
              visibility: 'private',
            });
          }
        },
        onError: () => {
          setIsGenerating(false);
        },
      },
    );
  };

  return (
    <Drawer
      title="Save Analysis Template"
      open={open}
      onClose={handleClose}
      width={700}
      placement="right"
      footer={
        isGenerating ? null : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20 }}>
            {onSaveAndSchedule && (
              <Button
                type="primary"
                onClick={handleSaveAndSchedule}
                loading={saveMutation.isLoading}
              >
                Save &amp; Schedule
              </Button>
            )}
            <Button type="primary" onClick={handleSave} loading={saveMutation.isLoading}>
              Save
            </Button>
          </div>
        )
      }
      destroyOnClose
    >
      {isGenerating ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            gap: 24,
          }}
        >
          <Spin size="large" />
          <div style={{ textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
              Generating analysis template...
            </div>
            <div style={{ fontSize: 14 }}>
              This may take a minute. We&apos;re analyzing your session to create reusable
              instructions.
            </div>
          </div>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            deep_mode: false,
            visibility: 'private',
          }}
        >
          <Form.Item
            label="Query"
            name="query"
            rules={[
              { required: true, message: 'Please enter a query' },
              { whitespace: true, message: 'Query cannot be empty' },
            ]}
          >
            <TextArea rows={3} placeholder="Enter the user-facing question for this analysis..." />
          </Form.Item>

          <Form.Item
            label="Instructions"
            name="extra_instructions"
            rules={[
              { required: true, message: 'Please enter instructions' },
              { whitespace: true, message: 'Instructions cannot be empty' },
            ]}
            help="These instructions will be used to reproduce this analysis. Markdown formatting is supported."
          >
            <TextArea
              rows={10}
              placeholder="Enter the analysis instructions..."
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>

          <Form.Item
            label={
              canSeeDeepMode ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Deep Analysis Mode</span>
                  <Text type="secondary">[internal]</Text>
                  <Tooltip title="Runs a more comprehensive, multi-step analysis. Increases runtime; use only for complex investigations.">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </div>
              ) : (
                'Deep Analysis Mode'
              )
            }
            name="deep_mode"
            valuePropName="checked"
          >
            {canSeeDeepMode ? (
              <Form.Item shouldUpdate noStyle>
                {() => {
                  const current = Boolean(form.getFieldValue('deep_mode'));
                  return (
                    <Switch
                      checked={current}
                      onChange={(nextChecked) => {
                        if (!current && nextChecked) {
                          Modal.confirm({
                            title: 'Enable Deep Analysis Mode?',
                            content:
                              'Deep Analysis Mode may take significantly longer to run. Use it only for complex investigations.',
                            okText: 'Enable',
                            cancelText: 'Cancel',
                            onOk: () => form.setFieldValue('deep_mode', true),
                            onCancel: () => form.setFieldValue('deep_mode', false),
                          });
                          return;
                        }
                        form.setFieldValue('deep_mode', Boolean(nextChecked));
                      }}
                    />
                  );
                }}
              </Form.Item>
            ) : null}
          </Form.Item>

          <Form.Item
            label="Visibility"
            name="visibility"
            rules={[{ required: true, message: 'Please select visibility' }]}
          >
            <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Radio value="private">Private (Only me)</Radio>
              <Radio value="team">Team (Everyone in my organization)</Radio>
              {canManageSysPrompt && <Radio value="recommended">Recommended (All customers)</Radio>}
            </Radio.Group>
          </Form.Item>

          <Form.Item label="Tags">
            <TagInput value={tags} onChange={setTags} maxTags={5} maxTagLength={32} />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
};
